import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

/** Shared CRM data layer. `role` restricts which accounts may sign in to this portal. */
export default function useCrm({ tokenKey, role }) {
  const [token, setToken] = useState(() => localStorage.getItem(tokenKey) || "");
  const [user, setUser] = useState(null);
  const [agents, setAgents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const isAdmin = role === "admin";

  const api = useMemo(() => {
    const inst = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
    inst.interceptors.response.use((r) => r, (e) => {
      if (e.response?.status === 401) { localStorage.removeItem(tokenKey); setToken(""); }
      return Promise.reject(e);
    });
    return inst;
  }, [token, tokenKey]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const me = await api.get("/auth/me");
      if (me.data.role !== role) {
        localStorage.removeItem(tokenKey); setToken(""); setUser(null);
        return;
      }
      setUser(me.data);
      const [l, s, a] = await Promise.all([api.get("/leads"), api.get("/dashboard/stats"), isAdmin ? api.get("/agents") : Promise.resolve({ data: [] })]);
      setLeads(l.data); setStats(s.data); setAgents(a.data);
    } catch (e) {
      if (e.response?.status !== 401) toast.error("Could not load CRM data.");
    } finally {
      setLoading(false);
    }
  }, [api, token, role, tokenKey, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const refreshStats = useCallback(async () => {
    try { const { data } = await api.get("/dashboard/stats"); setStats(data); } catch { /* non-critical */ }
  }, [api]);

  const replaceLead = (updated) => setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.user.role !== role) {
      const err = new Error("wrong portal");
      err.response = { data: { detail: isAdmin ? "This is an agent account. Please sign in at the agent portal (/agent)." : "This is an admin account. Please sign in at /admin." } };
      throw err;
    }
    localStorage.setItem(tokenKey, data.token);
    setUser(data.user);
    setToken(data.token);
  };

  const logout = () => { localStorage.removeItem(tokenKey); setToken(""); setUser(null); };

  const changePassword = async (current_password, new_password) => {
    const { data } = await api.post("/auth/change-password", { current_password, new_password });
    setUser(data.user);
    toast.success("Password updated");
    load();
  };

  const setStage = async (id, status) => {
    const prev = leads.find((l) => l.id === id);
    if (!prev || prev.status === status) return;
    replaceLead({ ...prev, status });
    try {
      const { data } = await api.patch(`/leads/${id}`, { status });
      replaceLead(data);
      toast.success(`${data.full_name} moved to ${status}`);
      refreshStats();
    } catch (e) {
      replaceLead(prev);
      toast.error(errMsg(e, "Could not update stage."));
    }
  };

  const reassign = async (id, agentId) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { assigned_agent_id: agentId });
      replaceLead(data);
      toast.success(data.assigned_agent_name ? `Reassigned to ${data.assigned_agent_name}` : "Recruit unassigned");
      const [a] = await Promise.all([api.get("/agents"), refreshStats()]);
      setAgents(a.data);
    } catch (e) {
      toast.error(errMsg(e, "Could not reassign recruit."));
    }
  };

  const addNote = async (id, body) => {
    try {
      const { data } = await api.post(`/leads/${id}/notes`, { body });
      replaceLead(data);
      toast.success("Note added");
    } catch (e) {
      toast.error(errMsg(e, "Could not add note."));
      throw e;
    }
  };

  const deleteNote = async (id, noteId) => {
    try {
      const { data } = await api.delete(`/leads/${id}/notes/${noteId}`);
      replaceLead(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not delete note."));
    }
  };

  const deleteLead = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Recruit deleted");
      refreshStats();
      return true;
    } catch (e) {
      toast.error(errMsg(e, "Could not delete recruit."));
      return false;
    }
  };

  const addAgent = async (payload) => {
    try {
      const { data } = await api.post("/agents", payload);
      setAgents((prev) => [...prev, data]);
      toast.success(data.invite_sent ? `${data.name} added — invite e-mail sent` : `${data.name} added to the rotation`);
      refreshStats();
      return data;
    } catch (e) {
      toast.error(errMsg(e, "Could not add agent."));
      return null;
    }
  };

  const inviteAgent = async (agent) => {
    try {
      const { data } = await api.post(`/agents/${agent.id}/invite`);
      setAgents((prev) => prev.map((a) => (a.id === agent.id ? { ...a, login_status: "invited" } : a)));
      if (data.invite_sent) toast.success(`Invite e-mail sent to ${agent.email}`);
      return data;
    } catch (e) {
      toast.error(errMsg(e, "Could not send invite."));
      return null;
    }
  };

  const toggleAgent = async (agent) => {
    try {
      const { data } = await api.patch(`/agents/${agent.id}`, { active: !agent.active });
      setAgents((prev) => prev.map((a) => (a.id === data.id ? { ...a, ...data } : a)));
      toast.success(data.active ? `${data.name} is back in rotation` : `${data.name} paused`);
      refreshStats();
    } catch (e) {
      toast.error(errMsg(e, "Could not update agent."));
    }
  };

  const removeAgent = async (agent) => {
    try {
      await api.delete(`/agents/${agent.id}`);
      toast.success(`${agent.name} removed from roster`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not remove agent."));
    }
  };

  return {
    api, token, user, agents, leads, stats, loading, load, login, logout, changePassword,
    setStage, reassign, addNote, deleteNote, deleteLead, addAgent, inviteAgent, toggleAgent, removeAgent,
  };
}
